import axios from "axios";
import { resolveApiBaseUrl } from "./apiBaseUrl";

// Public endpoints that should not send auth headers or trigger auth redirects.
// Extend this list as new unauthenticated endpoints are added.
const AUTH_PUBLIC_PATHS = ["/auth/login", "/auth/register", "/auth/refresh", "/health"];
const LEGACY_BEARER_AUTH = String(process.env.REACT_APP_LEGACY_BEARER_AUTH || "false").toLowerCase() === "true";
const CSRF_COOKIE_NAME = process.env.REACT_APP_CSRF_COOKIE_NAME || "glyder_csrf";
const CSRF_HEADER_NAME = process.env.REACT_APP_CSRF_HEADER_NAME || "X-CSRF-Token";
const API_DEBUG = String(process.env.REACT_APP_DEBUG_API || "false").toLowerCase() === "true";

// Guard to prevent multiple simultaneous redirects on 401 responses.
let isAuthRedirectInProgress = false;

const toRequestPath = (url = "") => {
  const value = String(url || "");
  if (!value) return "";
  try {
    if (value.startsWith("http://") || value.startsWith("https://")) {
      return new URL(value).pathname;
    }
  } catch (_err) {
    return value;
  }
  return value;
};

const resolveRequestUrl = (config = {}) => {
  const rawUrl = String(config?.url || "");
  const base = String(config?.baseURL || "").trim();
  if (!rawUrl) return base;
  if (rawUrl.startsWith("http://") || rawUrl.startsWith("https://")) return rawUrl;

  if (base.startsWith("http://") || base.startsWith("https://")) {
    try {
      return new URL(rawUrl, `${base}/`).toString();
    } catch (_err) {
      return `${base}${rawUrl}`;
    }
  }

  return `${base}${rawUrl}`;
};

const requestMetaFromConfig = (config = {}, fallbackBase = "") => {
  const method = String(config?.method || "get").toUpperCase();
  const path = toRequestPath(config?.url || "");
  const baseURL = String(config?.baseURL || fallbackBase || "");
  const url = resolveRequestUrl({ ...config, baseURL });
  return { method, path, url, baseURL };
};

const apiClient = axios.create({
  baseURL: resolveApiBaseUrl(process.env.REACT_APP_API_URL),
  withCredentials: true,
});

const isPublicPath = (url = "") => {
  try {
    const pathname = url.startsWith("http") ? new URL(url).pathname : url;
    return AUTH_PUBLIC_PATHS.some((publicPath) => pathname.startsWith(publicPath));
  } catch (_err) {
    return false;
  }
};

const readCookie = (name) => {
  if (typeof document === "undefined" || !document.cookie) return null;
  const prefix = `${encodeURIComponent(name)}=`;
  const parts = document.cookie.split(";").map((part) => part.trim());
  for (const part of parts) {
    if (part.startsWith(prefix)) {
      return decodeURIComponent(part.slice(prefix.length));
    }
  }
  return null;
};

apiClient.interceptors.request.use(
  (config) => {
    // Request config flag: skipAuth -> do not attach the Authorization header.
    const skipAuth = config?.skipAuth === true;
    if (skipAuth || isPublicPath(config?.url || "")) {
      return config;
    }

    config.withCredentials = true;
    if (LEGACY_BEARER_AUTH) {
      const token = localStorage.getItem("access_token");
      if (token) {
        config.headers = config.headers || {};
        if (!config.headers.Authorization) {
          config.headers.Authorization = `Bearer ${token}`;
        }
      }
    }

    const method = String(config?.method || "get").toUpperCase();
    if (["POST", "PUT", "PATCH", "DELETE"].includes(method)) {
      const csrfToken = readCookie(CSRF_COOKIE_NAME);
      config.headers = config.headers || {};
      if (csrfToken && !config.headers[CSRF_HEADER_NAME]) {
        config.headers[CSRF_HEADER_NAME] = csrfToken;
      }
    }

    if (API_DEBUG) {
      const meta = requestMetaFromConfig(config, apiClient.defaults.baseURL || "");
      if (meta.path.includes("/impact-themes")) {
        // Debug-only trace for diagnosing theme endpoint mismatches.
        // eslint-disable-next-line no-console
        console.info("[api] request", meta);
      }
    }
    return config;
  },
  (error) => Promise.reject(error)
);

apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    const { response, config } = error || {};
    const requestMeta = requestMetaFromConfig(config, apiClient.defaults.baseURL || "");
    const payload = response?.data || {};
    const statusCode = response?.status;
    const errorValue =
      typeof payload?.error === "string"
        ? payload.error
        : typeof payload?.detail?.detail === "string"
          ? payload.detail.detail
          : "";
    const detailValue =
      typeof payload?.detail === "string"
        ? payload.detail
        : errorValue || payload?.detail || "Request failed";
    const codeValue = payload?.code || payload?.detail?.code;
    const fieldErrors = payload?.field_errors || payload?.detail?.field_errors || {};
    const errorObj = new Error(detailValue || "Request failed");
    errorObj.status = statusCode;
    errorObj.code = codeValue;
    errorObj.fieldErrors = fieldErrors;
    errorObj.detail = detailValue;
    errorObj.method = requestMeta.method;
    errorObj.path = requestMeta.path;
    errorObj.url = requestMeta.url;
    errorObj.baseURL = requestMeta.baseURL;
    errorObj.responseBody = payload;

    if (API_DEBUG && requestMeta.path.includes("/impact-themes")) {
      // eslint-disable-next-line no-console
      console.info("[api] response_error", {
        method: requestMeta.method,
        path: requestMeta.path,
        url: requestMeta.url,
        status: statusCode || null,
        body: payload || null,
      });
    }

    // If no response (network error), propagate the original error for callers to handle.
    if (!response) {
      return Promise.reject(errorObj);
    }

    if (statusCode !== 401) {
      return Promise.reject(errorObj);
    }

    const requestUrl = config?.url || "";
    // Request config flag: skipAuthRedirect -> caller will handle 401 without a hard redirect.
    const skipRedirect = config?.skipAuthRedirect === true;

    if (isPublicPath(requestUrl) || skipRedirect) {
      return Promise.reject(errorObj);
    }

    if (!isAuthRedirectInProgress) {
      isAuthRedirectInProgress = true;
      localStorage.removeItem("user");
      if (LEGACY_BEARER_AUTH) {
        localStorage.removeItem("access_token");
      }
      if (window.location.pathname !== "/login") {
        window.location.href = "/login";
      }
    }

    return Promise.reject(errorObj);
  }
);

export const formatApiError = (
  error,
  {
    action = "Request failed",
    fallbackDetail = "Request failed.",
  } = {}
) => {
  const status = Number.isFinite(Number(error?.status)) ? Number(error?.status) : null;
  const method = String(error?.method || "").toUpperCase();
  const path = String(error?.path || "");
  const detail = String(error?.detail || error?.message || "").trim();
  const hasRequestContext = Boolean(method || path);
  let message = action;

  if (status) message += ` (${status})`;
  if (hasRequestContext) {
    message += `: ${method || "REQUEST"}${path ? ` ${path}` : ""}.`;
  } else {
    message += ".";
  }

  const normalizedDetail = detail.toLowerCase();
  const isBareNotFound = normalizedDetail === "not found";
  if (status === 404 && path.startsWith("/impact-themes")) {
    message += " Check API base URL configuration.";
  } else if (detail && !isBareNotFound) {
    message += ` ${detail}`;
  } else if (!detail) {
    message += ` ${fallbackDetail}`;
  }

  return message.trim();
};

export const getApiBaseUrl = () => apiClient.defaults.baseURL || "";

export default apiClient;
